import React, { useMemo, useState } from "react";
import Select, { components } from "react-select";
import { customStyles } from "../../helpers/style";

const CHECKBOX_GREEN = "#7ebc0a";

/** Group headers (virtual) */
const NO_ANSWER_GROUP = "NO_ANSWER_GROUP";
const ANSWERED_GROUP = "ANSWERED_GROUP";

/** Real dispositions */
const ANSWERED_CHILDREN = ["ANSWERED", "ANSWER LATER"];
const NO_ANSWER_CHILDREN = ["NO ANSWER", "BUSY", "CANCEL", "FAILED"];

const splitLabelAndNumber = (label) => {
  const s = String(label ?? "").trim();
  const match = s.match(/^(.*?)\s+(\d+)$/);
  if (match) return { text: match[1].trim(), number: match[2] };
  return { text: s, number: null };
};

const checkboxStyle = {
  width: 16,
  height: 16,
  borderRadius: 3,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxSizing: "border-box",
};

const Checkbox = ({ checked, onClick, readOnly, style: styleProp }) => (
  <div
    role="checkbox"
    aria-checked={checked}
    tabIndex={readOnly ? -1 : 0}
    onMouseDown={(e) => {
      if (!readOnly) {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(e);
      }
    }}
    style={{
      ...checkboxStyle,
      border: `2px solid ${checked ? CHECKBOX_GREEN : "#d0d0d0"}`,
      backgroundColor: checked ? CHECKBOX_GREEN : "#fff",
      cursor: readOnly ? "default" : "pointer",
      ...styleProp,
    }}
  >
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" style={{ display: "block" }}>
        <path d="M1 4l2.5 2.5L9 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
);

const ValueContainer = (props) => {
  const { getValue, hasValue, selectProps } = props;

  const values = getValue() || [];
  const realCount = values.filter(
    (v) => v.value !== NO_ANSWER_GROUP && v.value !== ANSWERED_GROUP
  ).length;

  const inputChild = Array.isArray(props.children) ? props.children[1] : null;

  return (
    <components.ValueContainer {...props}>
      {!hasValue ? (
        props.children
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              paddingLeft: 2,
            }}
          >
            {selectProps.selectedLabel?.(realCount) || `Selected ${realCount} options`}
          </div>
          <div style={{ flex: "0 0 auto" }}>{inputChild}</div>
        </div>
      )}
    </components.ValueContainer>
  );
};

const CheckboxOption = (props) => {
  const { data, isSelected, label, selectProps, setValue, getValue } = props;

  const isAnsweredGroup = data.value === ANSWERED_GROUP;
  const isNoAnswerGroup = data.value === NO_ANSWER_GROUP;

  const isChild = data.parent === ANSWERED_GROUP || data.parent === NO_ANSWER_GROUP;

  const answeredOpen = selectProps.answeredOpen;
  const setAnsweredOpen = selectProps.setAnsweredOpen;

  const noAnswerOpen = selectProps.noAnswerOpen;
  const setNoAnswerOpen = selectProps.setNoAnswerOpen;

  const renderGroupHeader = ({ open, setOpen }) => {
    const toggleOpen = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen((v) => !v);
    };

    const { text, number } = splitLabelAndNumber(label);
    return (
      <components.Option {...props}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: "1 1 0", cursor: "pointer" }} onMouseDown={toggleOpen} title={label}>
            <span
              style={{
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {text}
            </span>
            {number != null && <span style={{ flexShrink: 0, marginLeft: 4, fontWeight: 700 }}>{number}</span>}
          </div>
          <span style={{ cursor: "pointer", padding: "0 6px", fontSize: 14, flexShrink: 0 }} onMouseDown={toggleOpen}>
            {open ? "▾" : "▸"}
          </span>
        </div>
      </components.Option>
    );
  };

  if (isAnsweredGroup) {
    return renderGroupHeader({ open: answeredOpen, setOpen: setAnsweredOpen });
  }

  if (isNoAnswerGroup) {
    return renderGroupHeader({ open: noAnswerOpen, setOpen: setNoAnswerOpen });
  }

  const { text, number } = splitLabelAndNumber(label);
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", minWidth: 0, width: "100%" }}>
        <Checkbox checked={isSelected} readOnly style={{ marginRight: 8, flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: "1 1 0" }} title={label}>
          <span
            style={{
              paddingLeft: isChild ? 18 : 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {text}
          </span>
          {number != null && <span style={{ flexShrink: 0, marginLeft: 4 }}>{number}</span>}
        </div>
      </div>
    </components.Option>
  );
};

export default function DispositionSelect({
  graphics,
  input,
  countsAnswered,
  counts,
  filters,
  setFilters,
  disabled = false,
}) {
  const lang = +localStorage.getItem("atsLang") || 1;

  const [answeredOpen, setAnsweredOpen] = useState(false);
  const [noAnswerOpen, setNoAnswerOpen] = useState(false);

  const baseOptions = useMemo(() => {
    // ✅ consistent counts source
    const getCountRaw = (k) => {
      if (counts && counts[k] != null) return counts[k];
      if (k === "ANSWERED" && countsAnswered != null) return countsAnswered;
      return 0;
    };
    const toNum = (v) => {
      if (typeof v === "number") return v;
      const n = parseInt(String(v), 10);
      return Number.isNaN(n) ? 0 : n;
    };
    const countNum = (k) => toNum(getCountRaw(k));
    const countLabel = (k) => String(getCountRaw(k) ?? 0);

    const answeredSum = ANSWERED_CHILDREN.reduce((acc, k) => acc + countNum(k), 0);
    const noAnswerSum = NO_ANSWER_CHILDREN.reduce((acc, k) => acc + countNum(k), 0);

    return [
      // ✅ group headers
      { value: ANSWERED_GROUP, label: `${graphics["ANSWERED"][lang]} ${answeredSum}` },
      ...ANSWERED_CHILDREN.map((k) => ({
        value: k,
        label: `${graphics[k]?.[lang] || k} ${countLabel(k)}`,
        parent: ANSWERED_GROUP,
      })),

      { value: NO_ANSWER_GROUP, label: `${graphics["NO ANSWER"][lang]} ${noAnswerSum}` },
      ...NO_ANSWER_CHILDREN.map((k) => ({
        value: k,
        label: `${graphics[k]?.[lang] || k} ${countLabel(k)}`,
        parent: NO_ANSWER_GROUP,
      })),
    ];
  }, [graphics, lang, counts, countsAnswered]);

  const allOptionsByValue = useMemo(
    () => Object.fromEntries(baseOptions.map((o) => [o.value, o])),
    [baseOptions]
  );

  const options = useMemo(() => {
    return baseOptions.filter((o) => {
      if (o.parent === ANSWERED_GROUP) return answeredOpen;
      if (o.parent === NO_ANSWER_GROUP) return noAnswerOpen;
      return true;
    });
  }, [baseOptions, answeredOpen, noAnswerOpen]);

  const selectedValues = useMemo(() => {
    const raw = filters.disposition;
    const str = Array.isArray(raw) ? raw.join(",") : raw != null ? String(raw) : "";
    const set = new Set(str.split(",").map((s) => s.trim()).filter(Boolean));
    return baseOptions.filter((o) => set.has(o.value));
  }, [filters.disposition, baseOptions]);

  // group header appears selected if ANY child selected (and stays even if partial)
  const selectedSet = useMemo(() => new Set(selectedValues.map((v) => v.value)), [selectedValues]);

  const someAnsweredSelected = ANSWERED_CHILDREN.some((v) => selectedSet.has(v));
  const allAnsweredSelected = ANSWERED_CHILDREN.every((v) => selectedSet.has(v));

  const someNoAnswerSelected = NO_ANSWER_CHILDREN.some((v) => selectedSet.has(v));
  const allNoAnswerSelected = NO_ANSWER_CHILDREN.every((v) => selectedSet.has(v));

  const computedSelected = useMemo(() => {
    let base = [...selectedValues];

    // inject/remove Answered group header
    const hasAnsweredGroup = base.some((v) => v.value === ANSWERED_GROUP);
    if (someAnsweredSelected && !hasAnsweredGroup) {
      const g = baseOptions.find((o) => o.value === ANSWERED_GROUP);
      if (g) base.push(g);
    }
    if (!someAnsweredSelected) base = base.filter((v) => v.value !== ANSWERED_GROUP);

    // inject/remove No Answer group header
    const hasNoAnswerGroup = base.some((v) => v.value === NO_ANSWER_GROUP);
    if (someNoAnswerSelected && !hasNoAnswerGroup) {
      const g = baseOptions.find((o) => o.value === NO_ANSWER_GROUP);
      if (g) base.push(g);
    }
    if (!someNoAnswerSelected) base = base.filter((v) => v.value !== NO_ANSWER_GROUP);

    return base;
  }, [selectedValues, baseOptions, someAnsweredSelected, someNoAnswerSelected]);

  const onChange = (newValue) => {
    const nv = Array.isArray(newValue) ? newValue : [];
    const next = nv
      .map((o) => o.value)
      .filter((v) => v !== ANSWERED_GROUP && v !== NO_ANSWER_GROUP);

    setFilters("disposition", { target: { value: next.join(",") } });
  };

  const selectedLabel = (n) => {
    const template = input.selectedOptions?.[lang] || "Selected {n} options";
    return template.replace("{n}", String(n));
  };

  return (
    <Select
      isMulti
      isDisabled={disabled}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      options={options}
      value={computedSelected}
      onChange={onChange}
      placeholder={input.select[lang]}
      menuPortalTarget={document.body}
      styles={{
        ...customStyles,
        placeholder: (base) => ({
          ...base,
          color: "#000",
        }),
        option: (base, state) => ({
          ...base,
          border: "none",
          borderTop: "none",
          borderBottom: "none",
          backgroundColor: state.isFocused || state.isSelected ? "hsla(86, 86.40%, 68.20%, 0.50)" : "#fff",
          color: "#000",
          outline: "none",
          WebkitTapHighlightColor: "transparent",
        }),
      }}
      className="basic-single w-100"
      classNamePrefix="select"
      controlShouldRenderValue={true}
      components={{
        Option: CheckboxOption,
        ValueContainer,
        MultiValue: () => null,
      }}
      answeredOpen={answeredOpen}
      setAnsweredOpen={setAnsweredOpen}
      noAnswerOpen={noAnswerOpen}
      setNoAnswerOpen={setNoAnswerOpen}
      allOptionsByValue={allOptionsByValue}
      selectedLabel={selectedLabel}
      formatOptionLabel={(opt, { context }) => {
        if (context === "menu" && (opt.value === ANSWERED_GROUP || opt.value === NO_ANSWER_GROUP)) {
          const suffix =
            opt.value === ANSWERED_GROUP
              ? (allAnsweredSelected ? " (all)" : someAnsweredSelected ? " (partial)" : "")
              : (allNoAnswerSelected ? " (all)" : someNoAnswerSelected ? " (partial)" : "");
          return (
            <div style={{ fontWeight: 700 }}>
              {opt.label}
              {suffix}
            </div>
          );
        }
        return opt.label;
      }}
    />
  );
}